import {
  type MissionAssessmentListItemDto,
  type MissionAssessmentRequestSelectDto,
  MissionAssessmentRequestStatus,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AvatarList } from '@components/shared/avatar-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { errorToast } from '@hooks/toast.hook';
import { useDeleteMissionAssessmentRequest } from '@queries/mission-assessment-requests.query';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight, ClipboardCheckIcon, ClockIcon, FileTextIcon, Trash2Icon, XCircleIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { isOpenRequestStatus } from './ma-overview-coverage.util';
import {
  buildMissionRequestServiceAvatarItems,
  getMissionRequestCountryDisplay,
} from './ma-overview-request-tile.util';

export type RequestListItem = {
  assessment?: MissionAssessmentListItemDto;
  request: MissionAssessmentRequestSelectDto;
};

type RequestTileProps = {
  canRequestAssessment: boolean;
  isAssignedToYou: boolean;
  item: RequestListItem;
  onSelectAssessment: (assessmentId: string) => void;
  technologyId: string;
};

type RequestTileActionsProps = {
  assessment?: MissionAssessmentListItemDto;
  canRequestAssessment: boolean;
  isAssignedToYou: boolean;
  isDeletingRequest: boolean;
  onDelete: () => void;
  onSelectAssessment: (assessmentId: string) => void;
  requestStatus: MissionAssessmentRequestStatus;
};

const REQUEST_STATUS_CONFIG = {
  [MissionAssessmentRequestStatus.ASSESSED]: {
    badgeVariant: 'success' as const,
    icon: ClipboardCheckIcon,
    label: 'Assessed',
  },
  [MissionAssessmentRequestStatus.CANCELLED]: {
    badgeVariant: 'destructive' as const,
    icon: XCircleIcon,
    label: 'Cancelled',
  },
  [MissionAssessmentRequestStatus.IN_PROGRESS]: {
    badgeVariant: 'pending' as const,
    icon: ClockIcon,
    label: 'In Progress',
  },
  [MissionAssessmentRequestStatus.REQUESTED]: {
    badgeVariant: 'primary' as const,
    icon: FileTextIcon,
    label: 'Requested',
  },
};

const RequestTileActions: FC<RequestTileActionsProps> = (props) => {
  const {
    assessment,
    canRequestAssessment,
    isAssignedToYou,
    isDeletingRequest,
    onDelete,
    onSelectAssessment,
    requestStatus,
  } = props;
  const handleOpenAssessment = (): void => {
    if (!assessment || assessment.accessRestricted) {
      return;
    }

    onSelectAssessment(assessment.id);
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {assessment && !assessment.accessRestricted && (
        <IconButton.HoverReveal
          ariaLabel={isAssignedToYou ? 'Open your assessment' : 'Open assessment'}
          icon={<ArrowRight size={16} />}
          onClick={handleOpenAssessment}
          title={isAssignedToYou ? 'Open your assessment' : 'Open assessment'}
          variant="secondary"
        />
      )}

      {assessment?.accessRestricted && <span className="self-center text-xs text-gray-500">Assessment restricted</span>}

      {canRequestAssessment && isOpenRequestStatus(requestStatus) && (
        <IconButton.HoverReveal
          ariaLabel="Withdraw request"
          icon={<Trash2Icon size={16} />}
          loading={isDeletingRequest}
          onClick={onDelete}
          variant="secondary"
        />
      )}
    </div>
  );
};

export const RequestTile: FC<RequestTileProps> = (props) => {
  const { canRequestAssessment, isAssignedToYou, item, onSelectAssessment, technologyId } = props;
  const { assessment, request } = item;
  const requestConfig = REQUEST_STATUS_CONFIG[request.status];
  const RequestStatusIcon = requestConfig.icon;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { isPending: isDeletingRequest, mutateAsync: deleteMissionAssessmentRequest } =
    useDeleteMissionAssessmentRequest({
      missionAssessmentRequestId: request.id,
      technologyId,
    });
  const requestedText = useMemo(() => {
    return formatDistanceToNow(new Date(request.createdAt), { addSuffix: true });
  }, [request.createdAt]);
  const targetNationDisplay = useMemo(() => {
    return getMissionRequestCountryDisplay(request.veteranCountriesOfService);
  }, [request.veteranCountriesOfService]);
  const serviceAvatarItems = useMemo(() => {
    return buildMissionRequestServiceAvatarItems(request.veteranServices);
  }, [request.veteranServices]);

  return (
    <div id={`ma-request-${request.id}`}>
      <Tile
        className="border border-gray-200 shadow-none"
        contentClassName="gap-0"
        hoverReveal
      >
        <div className="flex flex-col gap-4">
          {/* Top row: status + match badges (left), actions (right) */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                size="xs"
                variant={requestConfig.badgeVariant}
              >
                <RequestStatusIcon size={14} />
                {requestConfig.label}
              </Badge>

              {request.isStrictMatch && (
                <TooltipComponent
                  className="text-xs"
                  side="top"
                  trigger={
                    <Badge
                      size="xs"
                      variant="outline"
                    >
                      Strict match
                    </Badge>
                  }
                >
                  Requires exact match for target nation and service criteria
                </TooltipComponent>
              )}

              {isAssignedToYou && (
                <Badge
                  size="xs"
                  variant="expert"
                >
                  Assigned to you
                </Badge>
              )}

              <span className="text-xs text-gray-500">Requested {requestedText}</span>
            </div>

            <RequestTileActions
              assessment={assessment}
              canRequestAssessment={canRequestAssessment}
              isAssignedToYou={isAssignedToYou}
              isDeletingRequest={isDeletingRequest}
              onDelete={() => setIsDeleteModalOpen(true)}
              onSelectAssessment={onSelectAssessment}
              requestStatus={request.status}
            />
          </div>

          {request.message && <div className="text-xs text-gray-600 line-clamp-2">{request.message}</div>}

          {/* Data fields */}
          <div className="grid grid-cols-2 items-end gap-3 @min-[56rem]/technology:grid-cols-4">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">{request.use.name}</div>
              <BlueLabel text="Use" />
            </div>

            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">{request.domain.name}</div>
              <BlueLabel text="Domain" />
            </div>

            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {targetNationDisplay.text ?? <span className="text-gray-400">Not specified</span>}
              </div>
              <BlueLabel text={targetNationDisplay.names.length > 1 ? 'Target Nations' : 'Target Nation'} />
            </div>

            <div className="min-w-0">
              {serviceAvatarItems.length > 0 ? (
                <AvatarList
                  items={serviceAvatarItems}
                  label={serviceAvatarItems.length > 1 ? 'Services' : 'Service'}
                  limit={3}
                  size="sm"
                />
              ) : (
                <>
                  <div className="text-sm font-medium text-gray-400">Not specified</div>
                  <BlueLabel text="Services" />
                </>
              )}
            </div>
          </div>
        </div>
      </Tile>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        itemName="assessment request"
        onConfirm={async () => {
          try {
            await deleteMissionAssessmentRequest();
          } catch (error) {
            errorToast({ description: error instanceof Error ? error.message : 'Failed to withdraw request.' });
          }
        }}
        setIsOpen={setIsDeleteModalOpen}
      />
    </div>
  );
};
